import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const initialForm = {
  email: '',
  password: ''
};

export default function LoginPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.email || !form.password) {
      setMessage({
        type: 'warning',
        text: 'Please fill in all fields.'
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        email: form.email.trim(),
        password: form.password
      };

      console.log('Sending login payload:', payload);

      const response = await api.post('/auth/login-email', payload);
      const data = response.data;

      if (!data || !response.status || response.status >= 400) {
        throw new Error(data?.message || 'Login failed');
      }

      // Store token and user info if provided
      if (typeof data === 'object' && data.token) {
        login(data.user || {}, data.token);
        console.log('User logged in successfully');
      }

      setMessage({
        type: 'success',
        text: typeof data === 'object' 
          ? (data.message || 'Login successful. Redirecting...') 
          : (data || 'Login successful. Redirecting...')
      });
      
      // Reset form
      setForm(initialForm);
      
      // Redirect to dashboard after 1.5 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed.';
      
      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to backend. Make sure the server is running on http://localhost:5000';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({
        type: 'danger',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="hero-banner">
        <div className="container">
          <h1 className="hero-title">
            Welcome <em className="gold-text">Back</em>
          </h1>
          <p className="text-white-50 mb-0">Sign in to your Pandian Store account to continue shopping.</p>
        </div>
      </div>

      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-xl-5">
            <div className="form-card">
              <h3 className="card-title">
                <i className="fas fa-sign-in-alt location-icon" /> Login
              </h3>

              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <label className="input-label" htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="text-input"
                    placeholder="Enter your email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="input-label" htmlFor="password">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className="text-input"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                </div>

                {message.text && (
                  <div className={`alert alert-${message.type} py-2`} role="alert">
                    {message.text}
                  </div>
                )}

                <button className="btn-gold submit-btn" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Signing In...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt" /> Sign In
                    </>
                  )}
                </button>
              </form>

              <div className="text-center mt-4">
                <p className="text-muted small">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-gold fw-600">
                    Create one
                  </Link>
                </p>
              </div>

              <div className="text-center mt-3">
                <Link to="/" className="text-decoration-none text-muted small">
                  <i className="fas fa-arrow-left me-1" /> Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
